import { useRef, useState } from "react";
import { toast } from "sonner";
import { Trash2, UploadCloud } from "lucide-react";
import api from "../lib/api";

const AdminGallery = ({ items, onRefresh }) => {
  const [title, setTitle] = useState("");
  const [caption, setCaption] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const pick = (f) => {
    setFile(f || null);
    setPreview(f ? URL.createObjectURL(f) : "");
  };

  const add = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Choose a photo first.");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: up } = await api.post("/admin/upload", fd);
      await api.post("/gallery", { title, caption, image: up.url });
      toast.success("Added to the gallery!");
      setTitle("");
      setCaption("");
      pick(null);
      if (fileRef.current) fileRef.current.value = "";
      onRefresh();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Upload failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/gallery/${id}`);
      toast.success("Photo removed.");
      onRefresh();
    } catch {
      toast.error("Could not remove this photo.");
    }
  };

  return (
    <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-12">
      <form
        onSubmit={add}
        className="h-fit rounded-3xl border border-line bg-white p-8 lg:col-span-4"
        data-testid="admin-gallery-form"
      >
        <h3 className="font-display text-2xl font-medium text-ink">Add a pet photo</h3>
        <p className="mt-2 text-sm leading-relaxed text-inkSoft">
          New photos appear in the public gallery right away.
        </p>
        <label className="mt-6 block cursor-pointer">
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-inkSoft">Photo (JPEG, PNG or WebP, under 8 MB)</span>
          <span
            className={`mt-2 flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed px-4 py-8 text-center transition-colors duration-200 ${
              preview ? "border-marmalade/50 bg-marmalade/5" : "border-line bg-cream hover:border-ink/25"
            }`}
          >
            {preview ? (
              <img src={preview} alt="Preview" className="max-h-40 rounded-xl object-contain" data-testid="gallery-preview" />
            ) : (
              <>
                <UploadCloud className="h-7 w-7 text-marmalade" />
                <span className="text-sm text-inkSoft">Click to choose a photo</span>
              </>
            )}
          </span>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={(e) => pick(e.target.files?.[0])}
            data-testid="admin-gallery-file"
            className="hidden"
          />
        </label>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Pet's name (e.g. Maple)"
          data-testid="admin-gallery-title"
          className="mt-4 w-full rounded-2xl border border-line bg-cream px-5 py-4 text-base text-ink placeholder:text-inkSoft/50 transition-[border-color] duration-200 focus:border-marmalade focus:outline-none"
          required
        />
        <input
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          placeholder="Caption (optional — e.g. Golden hour walk)"
          data-testid="admin-gallery-caption"
          className="mt-4 w-full rounded-2xl border border-line bg-cream px-5 py-4 text-base text-ink placeholder:text-inkSoft/50 transition-[border-color] duration-200 focus:border-marmalade focus:outline-none"
        />
        <button
          type="submit"
          disabled={busy}
          data-testid="admin-gallery-submit"
          className="mt-6 w-full rounded-full bg-forest py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-cream transition-[background-color] duration-300 hover:bg-ink disabled:opacity-60"
        >
          {busy ? "Uploading…" : "Add to gallery"}
        </button>
      </form>

      <div className="lg:col-span-8">
        {items.length === 0 ? (
          <div className="flex flex-col items-center gap-4 rounded-3xl border border-line bg-white px-6 py-20 text-center" data-testid="admin-gallery-empty">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-sand">
              <UploadCloud className="h-6 w-6 text-marmalade" />
            </span>
            <p className="font-display text-2xl text-ink">No photos yet</p>
            <p className="max-w-xs text-sm text-inkSoft">Upload the first happy tail to start the gallery.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
            {items.map((item) => (
              <div
                key={item.id}
                className="group relative overflow-hidden rounded-2xl border border-line bg-white"
                data-testid={`admin-gallery-item-${item.id}`}
              >
                <img src={item.image} alt={item.title} className="h-40 w-full object-cover" />
                <span className="absolute bottom-2 left-2 max-w-[70%] truncate rounded-full bg-cream/90 px-3 py-1 text-xs text-ink backdrop-blur">
                  {item.title}
                </span>
                <button
                  onClick={() => remove(item.id)}
                  data-testid={`admin-gallery-delete-${item.id}`}
                  aria-label={`Remove ${item.title}`}
                  className="absolute right-2 top-2 rounded-full bg-white/90 p-2 text-red-600 transition-colors duration-200 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminGallery;
